import {Tooltip} from '@mui/material';
import axios from 'axios';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {ReactComponent as ChevronLeftIcon} from '../../assets/icons/chevron-left.svg';
import FileSelectionPanel from '../../components/file-selection/FileSelectionPanel';
import {ConversationProvider} from '../../contexts/ConversationContext';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {DataSource, DocumentInDataSource} from '../../models/models';
import {collectionService} from '../../services/collection.service';
import {parseError} from '../../services/http.service';
import Chat from './Chat';
import {ChatSection, PanelSection, ToggleButton, WorkspaceContainer} from './ChatWorkspace.styled';

const ChatWorkspace: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [itemsByGroup, setItemsByGroup] = useState<Record<string, DocumentInDataSource[]>>({});
    const [loadingGroupIds, setLoadingGroupIds] = useState<(number | string)[]>([]);
    const [isLoadingDataSources, setIsLoadingDataSources] = useState(true);

    useEffect(() => {
        const fetchDataSources = async () => {
            try {
                setIsLoadingDataSources(true);
                const data = await collectionService.fetchCollections(0, 1000);
                setDataSources(data.content);
            } catch (error) {
                showSnackbar(parseError(error), 'error');
            } finally {
                setIsLoadingDataSources(false);
            }
        };
        void fetchDataSources();
    }, [showSnackbar]);

    const handleLoadGroupItems = useCallback(async (groupId: number) => {
        const key = String(groupId);
        if (loadingGroupIds.includes(groupId)) return;

        setLoadingGroupIds(prev => [...prev, groupId]);
        try {
            const { data } = await axios.get(`/documents/by-documents-collection/${groupId}`, {
                params: { page: 0, size: 100 }
            });
            const docs: DocumentInDataSource[] = data?.content ?? [];
            setItemsByGroup(prev => ({ ...prev, [key]: docs }));
        } catch (e) {
            showSnackbar(parseError(e), 'error');
        } finally {
            setLoadingGroupIds(prev => prev.filter(x => x !== groupId));
        }
    }, [loadingGroupIds, showSnackbar]);

    const togglePanel = () => {
        setIsPanelOpen(prev => !prev);
    };

    return (
        <ConversationProvider conversationId={id || null}>
            <WorkspaceContainer>
                <ChatSection $panelOpen={isPanelOpen}>
                    <Chat
                        dataSources={dataSources}
                        itemsByGroup={itemsByGroup}
                        loadingGroupIds={loadingGroupIds}
                        onLoadGroupItems={handleLoadGroupItems}
                    />
                </ChatSection>

                <PanelSection $isOpen={isPanelOpen}>
                    <FileSelectionPanel
                        isOpen={isPanelOpen}
                        dataSources={dataSources}
                        itemsByGroup={itemsByGroup}
                        loadingGroupIds={loadingGroupIds}
                        onLoadGroupItems={handleLoadGroupItems}
                        isLoadingDataSources={isLoadingDataSources}
                    />
                </PanelSection>

                <Tooltip title={isPanelOpen ? t('fileSelection.togglePanelOpen') : t('fileSelection.togglePanelClosed')}>
                    <ToggleButton $panelOpen={isPanelOpen} onClick={togglePanel} aria-label="Toggle file panel">
                        <ChevronLeftIcon />
                    </ToggleButton>
                </Tooltip>
            </WorkspaceContainer>
        </ConversationProvider>
    );
};

export default ChatWorkspace;
